import { initializeApp, getApp } from 'firebase/app';
import {
    getAuth,
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    onAuthStateChanged,
    signOut,
    fetchSignInMethodsForEmail,
    getMultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    sendPasswordResetEmail
} from 'firebase/auth';
import { RecaptchaVerifier } from 'firebase/auth';
import * as ciap from 'gcip-iap';
import { gcipConfig } from './config.js';

// Initialize Firebase App
const firebaseConfig = {
    apiKey: gcipConfig.apiKey,
    projectId: gcipConfig.projectId,
    authDomain: gcipConfig.authDomain
};

let auth = null;
try {
    auth = getAuth(getApp(gcipConfig.tenantId || undefined));
    // Tenant ID should be already set on initialization below.
} catch (e) {
    // Use different App names for every tenant so that
    // multiple users can be signed in at the same time (one per tenant).
    const app = initializeApp(firebaseConfig, gcipConfig.tenantId || '[DEFAULT]');
    auth = getAuth(app);
    // Set the tenant ID on the Auth instance.
    auth.tenantId = gcipConfig.tenantId || null;
}

// Get DOM elements
const authContainer = document.getElementById('auth-container');
const recaptchaContainer = document.getElementById('recaptcha-container-id');


// Authentication state
let currentUser = null;
const recaptchaVerifier = new RecaptchaVerifier(recaptchaContainer, {
    size: 'invisible'
}, auth);
recaptchaVerifier.render()

// MFA state management
let mfaVerificationId = null;
let mfaResolver = null;
let mfaPhoneNumber = null;

// Render login UI
function renderLoginUI() {
    authContainer.innerHTML = `
    <div class="auth-card">
      <h1 class="auth-title">Osaro Login</h1>
      <p class="auth-subtitle">Sign in to continue</p>
      
      <form id="email-login-form" class="auth-form">
        <div class="form-group">
          <label for="email">Email</label>
          <input 
            type="email" 
            id="email" 
            name="email" 
            required 
            autocomplete="email"
            placeholder="Enter your email"
          />
        </div>
        
        <div class="form-group">
          <label for="password">Password</label>
          <input 
            type="password" 
            id="password" 
            name="password" 
            required 
            autocomplete="current-password"
            placeholder="Enter your password"
          />
        </div>
        
        <div id="error-message" class="error-message" role="alert"></div>
        
        <button type="submit" class="btn btn-primary" id="email-login-btn">
          Sign In with Email
        </button>
      </form>
      
      <div class="divider">
        <span>OR</span>
      </div>
      
      <button id="google-login-btn" class="btn btn-google">
        <svg class="google-icon" viewBox="0 0 24 24" width="20" height="20">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
        Osaro Employee Login
      </button>
    </div>
  `;

    // Attach event listeners
    const emailForm = document.getElementById('email-login-form');
    const googleBtn = document.getElementById('google-login-btn');

    emailForm.addEventListener('submit', handleEmailLogin);
    googleBtn.addEventListener('click', handleGoogleLogin);
}

// Render authenticated UI
function renderAuthenticatedUI(user) {
    authContainer.innerHTML = `
    <div class="auth-card">
      <h1 class="auth-title">Welcome!</h1>
      <div class="user-info">
        <p><strong>Email:</strong> ${user.email || 'N/A'}</p>
        <p><strong>Display Name:</strong> ${user.displayName || 'N/A'}</p>
        <p><strong>UID:</strong> ${user.uid}</p>
      </div>
      <button id="logout-btn" class="btn btn-secondary">Sign Out</button>
    </div>
  `;

    document.getElementById('logout-btn').addEventListener('click', handleLogout);
}

// Render SMS verification UI
function renderSMSVerificationUI(phoneNumber) {
    // Mask phone number for privacy (show last 4 digits)
    let maskedPhone = 'your phone';
    if (phoneNumber && phoneNumber.length >= 4) {
        // Replace all characters except last 4 with asterisks
        const last4 = phoneNumber.slice(-4);
        const masked = '*'.repeat(Math.max(0, phoneNumber.length - 4));
        maskedPhone = masked + last4;
    } else if (phoneNumber) {
        maskedPhone = phoneNumber;
    }

    authContainer.innerHTML = `
    <div class="auth-card">
      <h1 class="auth-title">Verify Your Phone</h1>
      <p class="auth-subtitle">We sent a verification code to ${maskedPhone}</p>
      
      <form id="sms-verification-form" class="auth-form">
        <div class="form-group">
          <label for="verification-code">Verification Code</label>
          <input 
            type="text" 
            id="verification-code" 
            name="verification-code" 
            required 
            autocomplete="one-time-code"
            placeholder="Enter 6-digit code"
            maxlength="6"
            pattern="[0-9]{6}"
            inputmode="numeric"
          />
          <small class="form-hint">Enter the 6-digit code sent to your phone</small>
        </div>
        
        <div id="error-message" class="error-message" role="alert"></div>
        
        <button type="submit" class="btn btn-primary" id="verify-code-btn">
          Verify Code
        </button>
      </form>
      
      <div class="sms-verification-actions">
        <button id="resend-code-btn" class="btn btn-link">
          Resend Code
        </button>
        <button id="back-to-login-btn" class="btn btn-link">
          Back to Login
        </button>
      </div>
    </div>
  `;

    // Attach event listeners
    const verificationForm = document.getElementById('sms-verification-form');
    const resendBtn = document.getElementById('resend-code-btn');
    const backBtn = document.getElementById('back-to-login-btn');
    const codeInput = document.getElementById('verification-code');

    verificationForm.addEventListener('submit', handleSMSVerification);
    resendBtn.addEventListener('click', handleResendCode);
    backBtn.addEventListener('click', () => {
        // Reset MFA state
        mfaVerificationId = null;
        mfaResolver = null;
        mfaPhoneNumber = null;
        renderLoginUI();
    });

    // Auto-focus on code input
    codeInput.focus();

    // Auto-format: only allow numbers
    codeInput.addEventListener('input', (e) => {
        e.target.value = e.target.value.replace(/[^0-9]/g, '');
    });
}

// Handle email/password login
async function handleEmailLogin(e) {
    e.preventDefault();

    const email = document.getElementById('email').value;
    const password = document.getElementById('password').value;
    const errorMessage = document.getElementById('error-message');
    const loginBtn = document.getElementById('email-login-btn');
    const passwordInput = document.getElementById('password');

    errorMessage.textContent = '';
    errorMessage.style.display = 'none';
    loginBtn.disabled = true;
    loginBtn.textContent = 'Signing in...';

    // Check if password matches default password before attempting sign in
    if (gcipConfig.defaultPassword && password === gcipConfig.defaultPassword) {
        try {
            // Send password reset email
            await sendPasswordResetEmail(auth, email);
            
            // Show success message
            errorMessage.style.display = 'block';
            errorMessage.style.background = '#d4edda';
            errorMessage.style.color = '#155724';
            errorMessage.style.borderColor = '#c3e6cb';
            errorMessage.textContent = 'Default password detected. A password reset email has been sent to your email address. Please check your inbox and follow the instructions to set a new password.';
            
            // Clear the password field
            passwordInput.value = '';
            
            // Reset button state
            loginBtn.disabled = false;
            loginBtn.textContent = 'Sign In with Email';
            
            return; // Exit early, don't attempt sign-in
        } catch (resetError) {
            // Handle password reset email errors
            let resetErrorMsg = 'Failed to send password reset email. Please try again.';
            
            switch (resetError.code) {
                case 'auth/user-not-found':
                    resetErrorMsg = 'No account found with this email. Please contact your administrator.';
                    break;
                case 'auth/invalid-email':
                    resetErrorMsg = 'Invalid email address.';
                    break;
                case 'auth/too-many-requests':
                    resetErrorMsg = 'Too many requests. Please wait a moment before trying again.';
                    break;
                default:
                    resetErrorMsg = resetError.message || resetErrorMsg;
            }
            
            errorMessage.style.display = 'block';
            errorMessage.style.background = '#fee';
            errorMessage.style.color = '#c33';
            errorMessage.style.borderColor = '#fcc';
            errorMessage.textContent = resetErrorMsg;
            
            loginBtn.disabled = false;
            loginBtn.textContent = 'Sign In with Email';
            
            return; // Exit early
        }
    }

    try {
        // Check if user exists before attempting sign in
        const signInMethods = await fetchSignInMethodsForEmail(auth, email);

        if (signInMethods.length === 0) {
            throw new Error('No account found with this email. Please contact your administrator.');
        }

        // Attempt sign in
        await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
        let errorMsg = 'An error occurred during sign in.';

        switch (error.code) {
            case 'auth/multi-factor-auth-required':
                // Reset login button state
                loginBtn.disabled = false;
                loginBtn.textContent = 'Sign In with Email';

                try {
                    mfaResolver = getMultiFactorResolver(auth, error);

                    // Extract phone number from the first hint
                    if (mfaResolver.hints && mfaResolver.hints.length > 0) {
                        mfaPhoneNumber = mfaResolver.hints[0].phoneNumber || null;
                    }

                    const phoneInfoOptions = {
                        multiFactorHint: mfaResolver.hints[0],
                        session: mfaResolver.session
                    };
                    const phoneAuthProvider = new PhoneAuthProvider(auth);

                    // Send SMS verification code
                    mfaVerificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);

                    // Show SMS verification UI
                    renderSMSVerificationUI(mfaPhoneNumber);
                    return; // Exit early, verification will complete via UI
                } catch (mfaError) {
                    console.error('MFA error:', mfaError);
                    errorMsg = mfaError.message || 'Failed to send verification code. Please try again.';
                    errorMessage.textContent = errorMsg;
                    errorMessage.style.display = 'block';
                    return;
                }
            case 'auth/user-not-found':
                errorMsg = 'No account found with this email. Please contact your administrator.';
                break;
            case 'auth/wrong-password':
                errorMsg = 'Incorrect password. Please try again.';
                break;
            case 'auth/invalid-email':
                errorMsg = 'Invalid email address.';
                break;
            case 'auth/user-disabled':
                errorMsg = 'This account has been disabled.';
                break;
            case 'auth/too-many-requests':
                errorMsg = 'Too many failed attempts. Please try again later.';
                break;
            default:
                errorMsg = error.message || errorMsg;
        }

        errorMessage.textContent = errorMsg;
        errorMessage.style.display = 'block';
    } finally {
        loginBtn.disabled = false;
        loginBtn.textContent = 'Sign In with Email';
    }
}

// Handle Google login
async function handleGoogleLogin() {
    const googleBtn = document.getElementById('google-login-btn');
    const errorMessage = document.getElementById('error-message');

    if (errorMessage) {
        errorMessage.textContent = '';
        errorMessage.style.display = 'none';
    }

    googleBtn.disabled = true;
    googleBtn.innerHTML = `
    <svg class="google-icon" viewBox="0 0 24 24" width="20" height="20">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
    Signing in...
  `;

    try {
        const provider = new GoogleAuthProvider();
        if (gcipConfig.googleClientId && gcipConfig.googleClientId !== 'YOUR_GOOGLE_OAUTH_CLIENT_ID') {
            provider.setCustomParameters({
                client_id: gcipConfig.googleClientId
            });
        }
        await signInWithPopup(auth, provider);
        // Auth state change will handle UI update
    } catch (error) {
        let errorMsg = 'An error occurred during Google sign in.';

        switch (error.code) {
            case 'auth/account-exists-with-different-credential':
                errorMsg = 'An account already exists with this email using a different sign-in method.';
                break;
            default:
                errorMsg = error.message || errorMsg;
        }

        if (errorMessage) {
            errorMessage.textContent = errorMsg;
            errorMessage.style.display = 'block';
        } else {
            // If error message element doesn't exist, show alert
            alert(errorMsg);
        }
        
        googleBtn.disabled = false;
        googleBtn.innerHTML = `
      <svg class="google-icon" viewBox="0 0 24 24" width="20" height="20">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      Osaro Employee Login
    `;
    }
}

// Handle SMS verification
async function handleSMSVerification(e) {
    e.preventDefault();

    const verificationCode = document.getElementById('verification-code').value.trim();
    const errorMessage = document.getElementById('error-message');
    const verifyBtn = document.getElementById('verify-code-btn');

    errorMessage.textContent = '';
    errorMessage.style.display = 'none';

    // Validate code format
    if (!verificationCode || verificationCode.length !== 6 || !/^\d{6}$/.test(verificationCode)) {
        errorMessage.textContent = 'Please enter a valid 6-digit verification code.';
        errorMessage.style.display = 'block';
        return;
    }

    if (!mfaVerificationId || !mfaResolver) {
        errorMessage.textContent = 'Verification session expired. Please try signing in again.';
        errorMessage.style.display = 'block';
        renderLoginUI();
        return;
    }

    verifyBtn.disabled = true;
    verifyBtn.textContent = 'Verifying...';

    try {
        // Create credential with verification ID and code
        const cred = PhoneAuthProvider.credential(mfaVerificationId, verificationCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        // Complete sign-in
        await mfaResolver.resolveSignIn(multiFactorAssertion);

        // Success - auth state change will handle UI update
        // Reset MFA state
        mfaVerificationId = null;
        mfaResolver = null;
        mfaPhoneNumber = null;
    } catch (error) {
        let errorMsg = 'Verification failed. Please try again.';

        switch (error.code) {
            case 'auth/invalid-verification-code':
                errorMsg = 'Invalid verification code. Please check and try again.';
                break;
            case 'auth/code-expired':
                errorMsg = 'Verification code has expired. Please request a new code.';
                break;
            case 'auth/session-expired':
                errorMsg = 'Verification session expired. Please try signing in again.';
                mfaVerificationId = null;
                mfaResolver = null;
                mfaPhoneNumber = null;
                renderLoginUI();
                return;
            default:
                errorMsg = error.message || errorMsg;
        }

        errorMessage.textContent = errorMsg;
        errorMessage.style.display = 'block';

        // Clear the input for retry
        document.getElementById('verification-code').value = '';
        document.getElementById('verification-code').focus();
    } finally {
        verifyBtn.disabled = false;
        verifyBtn.textContent = 'Verify Code';
    }
}

// Handle resend verification code
async function handleResendCode() {
    const resendBtn = document.getElementById('resend-code-btn');
    const errorMessage = document.getElementById('error-message');

    if (!mfaResolver || !mfaResolver.hints || mfaResolver.hints.length === 0) {
        errorMessage.textContent = 'Session expired. Please try signing in again.';
        errorMessage.style.display = 'block';
        renderLoginUI();
        return;
    }

    resendBtn.disabled = true;
    resendBtn.textContent = 'Sending...';
    errorMessage.textContent = '';
    errorMessage.style.display = 'none';

    try {
        // Use multiFactorHint for resend (same as initial verification)
        const phoneInfoOptions = {
            multiFactorHint: mfaResolver.hints[0],
            session: mfaResolver.session
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);

        // Send new SMS verification code
        mfaVerificationId = await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier);

        // Show success message
        errorMessage.style.display = 'block';
        errorMessage.style.background = '#d4edda';
        errorMessage.style.color = '#155724';
        errorMessage.style.borderColor = '#c3e6cb';
        errorMessage.textContent = 'New verification code sent!';

        // Clear the input
        document.getElementById('verification-code').value = '';
        document.getElementById('verification-code').focus();

        // Reset message after 3 seconds
        setTimeout(() => {
            errorMessage.style.display = 'none';
            errorMessage.style.background = '#fee';
            errorMessage.style.color = '#c33';
            errorMessage.style.borderColor = '#fcc';
        }, 3000);
    } catch (error) {
        let errorMsg = 'Failed to resend code. Please try again.';

        switch (error.code) {
            case 'auth/too-many-requests':
                errorMsg = 'Too many requests. Please wait a moment before requesting a new code.';
                break;
            default:
                errorMsg = error.message || errorMsg;
        }

        errorMessage.textContent = errorMsg;
        errorMessage.style.display = 'block';
    } finally {
        resendBtn.disabled = false;
        resendBtn.textContent = 'Resend Code';
    }
}

// Handle logout
async function handleLogout() {
    try {
        await signOut(auth);
    } catch (error) {
        console.error('Error signing out:', error);
        alert('Error signing out. Please try again.');
    }
}

// Implement GCIP IAP AuthenticationHandler
const authenticationHandler = {
    getAuth(apiKey, tenantId) {
        return auth;
    },

    async startSignIn(authInstance, match) {
        // This will be called by GCIP IAP when authentication is needed
        // The UI is already rendered, so we just wait for user interaction
        return new Promise((resolve, reject) => {
            // Listen for auth state changes
            const unsubscribe = onAuthStateChanged(authInstance, (user) => {
                if (user) {
                    unsubscribe();
                    resolve({ user });
                }
            });

            // Set a timeout to prevent hanging
            setTimeout(() => {
                unsubscribe();
                reject(new Error('Sign in timeout'));
            }, 300000); // 5 minutes
        });
    },

    async completeSignOut() {
        await signOut(auth);
        renderLoginUI();
    },

    handleError(error) {
        console.error('Authentication error:', error);
        const errorMessage = document.getElementById('error-message');
        if (errorMessage) {
            errorMessage.textContent = error.message || 'An authentication error occurred.';
            errorMessage.style.display = 'block';
        }
    }
};
// Render UI first so error message element exists
renderLoginUI();

// Initialize GCIP IAP Authentication
const authentication = new ciap.Authentication(authenticationHandler);

// Start GCIP IAP
authentication.start();
